import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { categories, articles } from '@/mocks/data';

const articleImages: Record<string, string> = {
  '1': 'https://knowguide.github.io/api/search-image?query=Soft%20warm%20abstract%20illustration%20of%20anatomical%20education%20with%20gentle%20coral%20and%20peach%20tones%2C%20simple%20clean%20medical%20education%20style%2C%20minimal%20composition%20with%20organic%20flowing%20shapes%20representing%20body%20positivity%2C%20warm%20neutral%20background&width=800&height=500&seq=article-thumb-1&orientation=landscape',
  '2': 'https://knowguide.github.io/api/search-image?query=Artistic%20abstract%20representation%20of%20human%20body%20and%20wellness%20with%20soft%20peachy%20warm%20tones%2C%20flowing%20organic%20lines%20suggesting%20natural%20bodily%20rhythms%2C%20calm%20educational%20medical%20illustration%20style%2C%20gentle%20minimalist%20composition%20with%20warm%20neutral%20background&width=800&height=500&seq=article-thumb-2&orientation=landscape',
  '3': 'https://knowguide.github.io/api/search-image?query=Warm%20minimalist%20illustration%20of%20two%20people%20having%20a%20gentle%20caring%20conversation%2C%20soft%20coral%20and%20peach%20tones%2C%20emotional%20wellness%20theme%2C%20abstract%20flowing%20shapes%20suggesting%20connection%20and%20understanding%2C%20calm%20safe%20atmosphere%2C%20contemporary%20illustration%20style&width=800&height=500&seq=article-thumb-3&orientation=landscape',
  '4': 'https://knowguide.github.io/api/search-image?query=Minimalist%20abstract%20illustration%20representing%20communication%20and%20mutual%20understanding%2C%20two%20overlapping%20gentle%20circles%20in%20warm%20coral%20and%20peach%2C%20soft%20watercolor%20texture%2C%20calm%20and%20trusting%20atmosphere%2C%20contemporary%20art%20style%20with%20clean%20composition&width=800&height=500&seq=article-thumb-4&orientation=landscape',
  '5': 'https://knowguide.github.io/api/search-image?query=Clean%20minimalist%20medical%20illustration%20showing%20various%20contraceptive%20options%20in%20abstract%20geometric%20style%2C%20warm%20beige%20and%20sage%20green%20tones%2C%20educational%20yet%20approachable%20design%2C%20soft%20rounded%20shapes%2C%20calm%20professional%20medical%20aesthetic&width=800&height=500&seq=article-thumb-5&orientation=landscape',
  '6': 'https://knowguide.github.io/api/search-image?query=Abstract%20medical%20infographic%20style%20illustration%20about%20health%20protection%2C%20soft%20sage%20green%20and%20warm%20beige%20tones%2C%20shield-like%20geometric%20shapes%20suggesting%20protection%2C%20clean%20educational%20aesthetic%20with%20minimal%20composition%2C%20calm%20reassuring%20atmosphere&width=800&height=500&seq=article-thumb-6&orientation=landscape',
  '7': 'https://knowguide.github.io/api/search-image?query=Abstract%20illustration%20breaking%20stereotypes%20with%20warm%20coral%20tones%2C%20fragmented%20geometric%20shapes%20dissolving%20into%20soft%20organic%20forms%2C%20symbolizing%20myth-breaking%20and%20truth%2C%20contemporary%20minimalist%20art%20style%2C%20soft%20peachy%20background&width=800&height=500&seq=article-thumb-7&orientation=landscape',
  '8': 'https://knowguide.github.io/api/search-image?query=Conceptual%20minimalist%20illustration%20of%20myths%20being%20dispelled%2C%20lightbulb%20moment%20theme%20with%20warm%20amber%20glow%2C%20abstract%20geometric%20shapes%20transitioning%20from%20confusion%20to%20clarity%2C%20soft%20peach%20and%20warm%20beige%20tones%2C%20educational%20yet%20artistic%20style&width=800&height=500&seq=article-thumb-8&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === activeSlug);

  return (
    <Layout>
      <section className="pt-24 pb-16 md:pb-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium">{activeCategory.name}</span>
          </nav>

          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
              {activeCategory.name}
            </h1>
            <p className="text-foreground-600 max-w-2xl">
              {activeCategory.description}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-1 py-1 rounded-full`}
              >
                <span
                  className={`block px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                  }`}
                >
                  {cat.name}
                </span>
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:shadow-sm transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={articleImages[article.id]}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3 text-xs text-foreground-500">
                      <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 font-medium">
                        {activeCategory.name}
                      </span>
                      <span>{article.readTime}</span>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-2 font-heading group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 pt-4 border-t border-background-200/70">
                      <span className="text-xs text-foreground-500">
                        {article.author} · {article.publishDate}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-article-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}